import { api } from "@/lib/api-client";
import { uploadContentType, uploadFileWithXHR } from "@/lib/uploadFileWithXHR";
import {
  bytesToWholeMegabytes,
  uploadSizeLimitFromError,
} from "@/lib/uploadSizeLimit";
import { sentryLog } from "@/utils/sentry-utils";
import { addToast, Button, Progress } from "@heroui/react";
import { useForm } from "@mantine/form";
import { useState } from "react";
import { LimeInput } from "./LimeInput";
import { LimeAlert } from "./LimeAlert";
import { CloudUpload } from "lucide-react";
import { Trans } from "@lingui/react/macro";
import { t } from "@lingui/core/macro";
import { LimeDropzone } from "../LimeDropzone";
import { LimeCollapse } from "./LimeCollapse";

export const CustomerFileUploadForm = ({
  onSuccessfulUpload,
  customerId,
  userAppointmentId,
  customerNoteId,
}: {
  onSuccessfulUpload?: () => void;
  customerId: number;
  userAppointmentId?: number;
  customerNoteId?: number;
}) => {
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const {
    mutateAsync: getCustomerFileUploadPresignedUrl,
    isPending: isGettingPresignedUrl,
    processedErrorMessage: getPresignedUrlError,
  } = api.storage.useGetCustomerFileUploadPresignedUrl();

  const {
    mutateAsync: completeCustomerFileUpload,
    isPending: isCompletingCustomerFileUpload,
    processedErrorMessage: completeCustomerFileUploadError,
  } = api.storage.useCompleteCustomerFileUpload();

  const form = useForm<{
    filePath: string;
    label: string;
  }>({
    initialValues: {
      filePath: "",
      label: "",
    },

    validate: {
      filePath: (value) => {
        if (!value) {
          return t`Datoteka je obvezna`;
        }
      },
    },
  });

  const setFileSelection = (file: File | null) => {
    if (!file) {
      setSelectedFile(null);
      form.setFieldValue("filePath", "");
      return;
    }

    setSelectedFile(file);
    form.setFieldValue("filePath", file.name);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    const formData = new FormData(e.target as HTMLFormElement);
    const label = formData.get("fileLabel") as string;

    if (!selectedFile || selectedFile.size === 0) {
      return;
    }

    setIsUploading(true);
    setUploadProgress(0);

    const presignedUrlData = await (async () => {
      try {
        const response = await getCustomerFileUploadPresignedUrl({
          customerId,
          query: {
            contentType: uploadContentType(selectedFile),
            fileNameWithExtension: selectedFile.name,
            fileSize: selectedFile.size,
            userAppointmentId,
            customerNoteId,
          },
        });

        return response;
      } catch (error) {
        const sizeLimit = uploadSizeLimitFromError(error);
        const maxMegabytes =
          sizeLimit === null ? null : bytesToWholeMegabytes(sizeLimit);
        addToast({
          title: t`Napaka`,
          description:
            maxMegabytes === null
              ? t`Prišlo je do napake pri nalaganju datoteke`
              : t`Datoteka je prevelika. Največja velikost je ${maxMegabytes} MB.`,
          color: "danger",
        });

        setIsUploading(false);
        setUploadProgress(0);
        setSelectedFile(null);
        form.reset();

        return null;
      }
    })();

    if (!presignedUrlData) {
      return;
    }

    try {
      await uploadFileWithXHR({
        file: selectedFile,
        presignedUrl: presignedUrlData.presignedUrl,
        onProgress: (percent) => {
          setUploadProgress(percent);
        },
      });

      await completeCustomerFileUpload({
        customerId,
        body: {
          fileId: presignedUrlData.fileId,
          isSuccess: true,
          label,
          customerNoteId,
        },
      });

      const storedFileName = presignedUrlData.fileName;

      addToast({
        title: t`Datoteka naložena`,
        description:
          storedFileName === selectedFile.name
            ? t`Datoteka je bila uspešno naložena`
            : t`Datoteka s tem imenom že obstaja, zato je bila shranjena kot ${storedFileName}`,
        color: "success",
      });

      onSuccessfulUpload?.();
    } catch (error) {
      addToast({
        title: t`Napaka`,
        description: t`Prišlo je do napake pri nalaganju datoteke`,
        color: "danger",
      });

      await completeCustomerFileUpload({
        customerId,
        body: {
          fileId: presignedUrlData.fileId,
          isSuccess: false,
        },
      });
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setSelectedFile(null);

      form.reset();
    }
  };

  return (
    <form
      className="flex flex-col gap-2"
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();

        form.validate();

        if (form.isValid()) {
          handleSubmit(e);
        }
      }}
    >
      <div>
        <LimeDropzone
          dropzoneProps={{
            onDrop: (acceptedFiles) => {
              const file = acceptedFiles[0];
              setFileSelection(file ?? null);
            },
            maxFiles: 1,
            onError: (error) => {
              sentryLog.error(
                "CustomerFileUploadForm Dropzone validation error",
                { error },
              );
            },
            src: selectedFile ? [selectedFile] : undefined,
          }}
        />
        <LimeCollapse isOpen={!!form.errors.filePath}>
          <p className="text-danger text-xs font-medium">
            {form.errors.filePath}
          </p>
        </LimeCollapse>
      </div>

      {isUploading && (
        <Progress
          aria-label="Uploading..."
          value={uploadProgress}
          showValueLabel
          className="mb-2"
          color="primary"
        />
      )}

      <LimeInput
        type="text"
        name="fileLabel"
        label={t`Naziv datoteke`}
        {...form.getInputProps("label")}
      />

      <LimeAlert
        className="mb-1"
        message={getPresignedUrlError ?? completeCustomerFileUploadError}
      />

      <Button
        color="primary"
        variant="bordered"
        startContent={<CloudUpload size={16} />}
        type="submit"
        key={"upload-customer-file-button"}
        isLoading={
          isGettingPresignedUrl || isCompletingCustomerFileUpload || isUploading
        }
      >
        <Trans>Naloži datoteko</Trans>
      </Button>
    </form>
  );
};
